<script setup lang="ts">
import { printVoucher } from '@/modules/voucher/voucherPOSService'
import { useVoucherPOSStore } from '@/stores/vouchers/voucherPOSStore'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { useDeviceManagementStore } from '@nsftx/seven-client-retail-vue-dm'
import {
  useVoucherStore,
  voucherErrorParser,
  vouchersApiService,
} from '@nsftx/seven-client-retail-vue-sgp'
import {
  NXGDSButton,
  NXGDSButtonVariants,
  NXGDSNotificationVariants,
} from '@nsftx/seven-nxg-design-system'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import {
  recordVoucherRedeemTransaction,
  recordVoucherRevokeTransaction,
} from '@/modules/calculator/calculatorTransactionService'

const LOG_PREFIX = '[VoucherDetailsModal]'

const { t } = useI18n()

const { appData } = storeToRefs(useDeviceManagementStore())
const { showNotification } = useNotificationsStore()
const { closeVoucherDetailsModal } = useVoucherPOSStore()
const { voucher, voucherPin } = storeToRefs(useVoucherStore())

const handleRevokeVoucher = async () => {
  try {
    await vouchersApiService.revokeVoucher(
      appData.value!.betshop.companyUuid,
      voucher.value!.id,
      voucherPin.value ?? '',
    )

    logService.info(`${LOG_PREFIX} Voucher revoked successfully`, {
      code: 'ST_VOUCHER_REVOKE_SUCCESS',
    })

    if (voucher.value) {
      recordVoucherRevokeTransaction(voucher.value)
    }

    showNotification({
      variant: NXGDSNotificationVariants.success,
      text: [t('voucher_revoke_success')],
    })

    closeVoucherDetailsModal()
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to revoke voucher`, {
      ...voucherErrorParser.parseUpstream(err),
      code: 'ST_VOUCHER_REDEEM_ERROR',
    })
    showNotification({
      variant: NXGDSNotificationVariants.error,
      text: [t('voucher_revoke_failed')],
    })
  }
}

const handleRedeemVoucher = async () => {
  try {
    await vouchersApiService.redeemVoucher(
      appData.value!.betshop.companyUuid,
      voucher.value!.id,
      voucherPin.value ?? '',
    )

    logService.info(`${LOG_PREFIX} Voucher redeemed successfully`, {
      code: 'ST_VOUCHER_REDEEM_SUCCESS',
    })

    await printVoucher(voucher.value!, 'payout')

    recordVoucherRedeemTransaction(voucher.value!)

    closeVoucherDetailsModal()
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to redeem voucher`, {
      ...voucherErrorParser.parseUpstream(err),
      code: 'ST_VOUCHER_REDEEM_ERROR',
    })
    showNotification({
      variant: NXGDSNotificationVariants.error,
      text: [t('voucher_redeem_failed')],
    })
  }
}
</script>

<template>
  <div class="ss-voucher-details-modal__actions">
    <NXGDSButton :variant="NXGDSButtonVariants.negative" @click="handleRevokeVoucher">
      {{ t('voucher_details_footer_revoke') }}
    </NXGDSButton>
    <NXGDSButton :variant="NXGDSButtonVariants.primary" @click="handleRedeemVoucher">
      {{ t('voucher_details_footer_redeem') }}
    </NXGDSButton>
  </div>
</template>

<style lang="scss">
.ss-voucher-details-modal {
  &__actions {
    display: flex;
    justify-content: space-between;
  }
}
</style>
